import type { FC } from "react";
import { useState } from "react";
import { FaPaypal, FaRegCreditCard } from "react-icons/fa6";

import ButtonPrimary from "@/shared/Button/ButtonPrimary";
import ButtonSecondary from "@/shared/Button/ButtonSecondary";
import FormItem from "@/shared/FormItem";
import Input from "@/shared/Input/Input";
import Radio from "@/shared/Radio/Radio";

interface Props {
  isActive: boolean;
  onCloseActive: () => void;
  onOpenActive: () => void;
  onPaymentMethodChange: (paymentMethod: number) => void;
}

const methodIdMap: { [key: string]: number } = {
  hosted: 1,
  payroll: 2,
  paypal: 3,
  card: 4,
};

const PaymentMethod: FC<Props> = ({
  isActive,
  onCloseActive,
  onPaymentMethodChange,
}) => {
  const [selectedMethod, setSelectedMethod] = useState<string>("hosted");

  const handleMethodChange = (value: string) => {
    setSelectedMethod(value);
    onPaymentMethodChange(methodIdMap[value] ?? 0);
  };

  const renderDebitCredit = () => {
    const active = selectedMethod === "Credit-Card";
    return (
      <div className="flex items-start space-x-4 sm:space-x-6">
        <Radio
          className="pt-3.5"
          name="payment-method"
          id="Credit-Card"
          checked={active}
          onChange={handleMethodChange}
        />
        <div className="flex-1">
          <div className="flex items-center space-x-4 sm:space-x-6">
            <div
              className={`rounded-xl border-2 p-2.5 ${active ? "border-primary" : "border-neutral-300"}`}
            >
              <FaRegCreditCard className="text-3xl" />
            </div>
            <p className="font-medium">Debit / Credit Card</p>
          </div>
          <div
            className={`mb-4 mt-6 space-y-3 sm:space-y-5 ${active ? "block" : "hidden"}`}
          >
            <div className="max-w-lg">
              <FormItem label="Card number">
                <Input
                  autoComplete="off"
                  rounded="rounded-lg"
                  sizeClass="h-12 px-4 py-3"
                  className="border-neutral-300 bg-transparent placeholder:text-neutral-500 focus:border-primary"
                  type="text"
                />
              </FormItem>
            </div>
            <div className="max-w-lg">
              <FormItem label="Card holder name">
                <Input
                  autoComplete="off"
                  rounded="rounded-lg"
                  sizeClass="h-12 px-4 py-3"
                  className="border-neutral-300 bg-transparent placeholder:text-neutral-500 focus:border-primary"
                  type="text"
                />
              </FormItem>
            </div>
            <div className="flex flex-col space-y-3 sm:flex-row sm:space-x-3 sm:space-y-0">
              <div className="sm:w-2/3">
                <FormItem label="Expiration date (MM/YY)">
                  <Input
                    autoComplete="off"
                    rounded="rounded-lg"
                    sizeClass="h-12 px-4 py-3"
                    className="border-neutral-300 bg-transparent placeholder:text-neutral-500 focus:border-primary"
                    placeholder="MM/YY"
                  />
                </FormItem>
              </div>
              <div className="flex-1">
                <FormItem label="CVC">
                  <Input
                    autoComplete="off"
                    rounded="rounded-lg"
                    sizeClass="h-12 px-4 py-3"
                    className="border-neutral-300 bg-transparent placeholder:text-neutral-500 focus:border-primary"
                    placeholder="CVC"
                  />
                </FormItem>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderWallet = () => {
    const active = selectedMethod === "Wallet";
    return (
      <div className="flex items-start space-x-4 sm:space-x-6">
        <Radio
          className="pt-3.5"
          name="payment-method"
          id="Wallet"
          checked={active}
          onChange={handleMethodChange}
        />
        <div className="flex-1">
          <div className="flex items-center space-x-4 sm:space-x-6">
            <div
              className={`rounded-xl border-2 p-2.5 ${active ? "border-primary" : "border-neutral-300"}`}
            >
              <FaPaypal className="text-3xl" />
            </div>
            <p className="font-medium">Paypal</p>
          </div>
          <div className={`mb-4 mt-6 space-y-6 ${active ? "block" : "hidden"}`}>
            <div className="max-w-lg">
              <FormItem label="PayPal email">
                <Input
                  autoComplete="off"
                  rounded="rounded-lg"
                  sizeClass="h-12 px-4 py-3"
                  className="border-neutral-300 bg-transparent placeholder:text-neutral-500 focus:border-primary"
                  type="text"
                />
              </FormItem>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-neutral-300">
      {/* 1. Hosted / Crypto */}
      <div className="flex flex-col items-center p-6 sm:flex-row">
        <Radio
          name="payment-method"
          id="hosted"
          checked={selectedMethod === "hosted"}
          onChange={handleMethodChange}
        />
        <span className="ml-2 hidden sm:block">
          <img
            src="Relm_Black.png"
            alt="RELM"
            className="object-center size-full h-8 object-contain"
          />
          <h3 className="uppercase tracking-tight">HOSTED</h3>
        </span>
        <div className="flex w-full items-center justify-between">
          <div className="sm:ml-8">
            <h3 className="uppercase tracking-tight">Pay with Crypto</h3>
            <div className="mt-1 text-sm font-semibold">
              <span>USDT, USDC, BTC, ETH and other crypto currencies.</span>
            </div>
          </div>
        </div>
      </div>
      <div className="sm:ml-8 pb-4" style={{ paddingRight: "20px" }}>
        <span>
          By continuing, I agree to Relm's{" "}
          <span style={{ textDecoration: "underline" }}>T&Cs</span> and confirm
          that I have read the{" "}
          <span style={{ textDecoration: "underline" }}>Privacy Notice</span>.
        </span>
        <span> Digital asset payments may be held for review.</span>
        <br />
        <span style={{ paddingTop: "10px", display: "block" }}>
          You will be redirected to Relm to complete your purchase securely.
        </span>
      </div>

      {/* 2. Relm Payout Widget */}
      {/* <div
        className={`flex flex-col items-center border-t border-neutral-300 p-6 sm:flex-row ${selectedMethod === "widget" ? "bg-neutral-50" : ""}`}
      >
        <Radio
          name="payment-method"
          id="payroll"
          checked={selectedMethod === "payroll"}
          onChange={handleMethodChange}
        />
        <span className="ml-2 hidden sm:block">
          <img
            src="Relm_Black.png"
            alt="RELM"
            className="object-center size-full h-8 object-contain"
          />
          <h3 className="uppercase tracking-tight">WIDGET</h3>
        </span>
        <div className="flex w-full items-center justify-between">
          <div className="sm:ml-8">
            <h3 className="uppercase tracking-tight">Payroll with Relm</h3>
            <div className="mt-1 text-sm font-semibold">
              <span>Pay multiple employees or beneficiaries in one go</span>
            </div>
          </div>
        </div>
      </div> */}

      {/* 3. PayPal */}
      <div className="flex flex-col items-center border-t border-neutral-300 p-6 sm:flex-row">
        <Radio
          name="payment-method"
          id="paypal"
          checked={selectedMethod === "paypal"}
          onChange={handleMethodChange}
        />
        <span className="ml-2 hidden sm:block">
          <img
            src="pp_standard.png"
            alt="PayPal"
            className="object-center size-full h-9 object-contain"
          />
        </span>
        <div className="flex w-full items-center justify-between">
          <div className="sm:ml-8">
            <h3 className="uppercase tracking-tight">Pay with Paypal</h3>
            <div className="mt-1 text-sm font-semibold">
              <span>Pay with Paypal</span>
            </div>
          </div>
        </div>
      </div>

      {/* 4. Card */}
      <div className="flex flex-col items-center border-t border-neutral-300 p-6 sm:flex-row">
        <Radio
          name="payment-method"
          id="card"
          checked={selectedMethod === "card"}
          onChange={handleMethodChange}
        />
        <span className="ml-2 hidden sm:block">
          <img
            src="cards.png"
            alt="Cards"
            className="object-center size-full h-9 object-contain"
          />
        </span>
        <div className="flex w-full items-center justify-between">
          <div className="sm:ml-8">
            <h3 className="uppercase tracking-tight">Pay with Card</h3>
            <div className="mt-1 text-sm font-semibold">
              <span>Visa / Mastercard</span>
            </div>
          </div>
        </div>
      </div>

      {/* 5. Expanded Credit-Card / Wallet forms */}
      <div
        className={`space-y-6 border-t border-neutral-300 px-6 py-7 ${isActive ? "block" : "hidden"}`}
      >
        <div>{renderDebitCredit()}</div>
        <div>{renderWallet()}</div>
        <div className="flex pt-6">
          <ButtonPrimary className="w-full max-w-[240px]">
            Confirm order
          </ButtonPrimary>
          <ButtonSecondary className="ml-3" onClick={onCloseActive}>
            Cancel
          </ButtonSecondary>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethod;
